import { useState } from 'react';

const MAX_PER_CATEGORY = 200;
const MAX_WORD_LENGTH = 50;

interface NgWordSectionProps {
  title: string;
  words: string[];
  onChange: (next: string[]) => void;
}

export const NgWordSection: React.FC<NgWordSectionProps> = ({ title, words, onChange }) => {
  const [input, setInput] = useState('');
  const [error, setError] = useState<string | null>(null);

  const trimmed = input.trim();
  const atCapacity = words.length >= MAX_PER_CATEGORY;
  const canAdd = trimmed.length > 0 && !atCapacity;

  const handleAdd = () => {
    setError(null);
    if (!trimmed) return;
    if (trimmed.length > MAX_WORD_LENGTH) {
      setError(`50 文字以内で入力してください`);
      return;
    }
    if (words.includes(trimmed)) {
      setError('既に登録されています');
      return;
    }
    if (atCapacity) {
      setError(`最大 ${MAX_PER_CATEGORY} 件までです`);
      return;
    }
    onChange([...words, trimmed]);
    setInput('');
  };

  const handleRemove = (word: string) => {
    onChange(words.filter((w) => w !== word));
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-slate-700">{title}</h3>
        <span className="text-xs text-slate-500">
          {words.length} / {MAX_PER_CATEGORY}
        </span>
      </div>

      <div className="flex flex-wrap gap-2">
        {words.length === 0 && <span className="text-xs text-slate-400">登録なし</span>}
        {words.map((word) => (
          <span
            key={word}
            className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-700"
          >
            {word}
            <button
              type="button"
              aria-label={`${word} を削除`}
              onClick={() => handleRemove(word)}
              className="text-slate-500 hover:text-red-600"
            >
              ×
            </button>
          </span>
        ))}
      </div>

      <div className="flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            setError(null);
          }}
          onKeyDown={(e) => {
            // IME 変換確定の Enter を誤検知しないよう isComposing をチェック
            if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
              e.preventDefault();
              handleAdd();
            }
          }}
          placeholder="NG ワードを入力"
          className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          type="button"
          onClick={handleAdd}
          disabled={!canAdd}
          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          追加
        </button>
      </div>

      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
};
